import React, { useMemo } from "react";
import dayjs from "dayjs";
import BoardCard from "components/dashboard/BoardCard";
import { DATE_FORMAT, DOCUMENT_CATEGORY_UUID } from "config/constant";
import { DashboardTable } from "../DashboardTable";
import DocumentStatus from "components/document/DocumentStatus";
import styled, { css } from "styled-components";
import DashboardStastic from "../DashboardStastic";
import { useTheme } from "styled-components";
import {
  CancelIcon,
  FileCheckIcon,
  FileLockIcon,
  HourglassIcon,
} from "assets/icons";
import DashboardTab from "../DashboardTab";
import Tab from "components/foundation/Tab";
import { ROUTER_PATH } from "config/path";
import useDocumentStatusCountQuery from "features/document/hooks/queries/useDocumentStatusCountQuery";
import {
  DocumentMyDeskResponse,
  PageResponseDocumentMyDeskResponse,
  UserDetailResponse,
} from "apis/data-contracts";
import { To, useNavigate } from "react-router-dom";
import useLatestWorkQuery from "features/document/hooks/queries/useLatestWorkQuery";
import useLatestAnalysisQuery from "features/document/hooks/queries/useLatestAnalysisQuery";
import { useRecoilState } from "recoil";
import { dashboardMyDeskTabIndexState } from "store/dashboard";
import useUserDetailQuery from "features/user/hooks/queries/useUserDetailQuery";
import { EDITABLE_DOCUMENT_STATUS_LIST } from "config/documentConstant";
import { modalStore } from "components/Common/Alert/AlertManager";
import useMediaView from "features/common/hooks/useMediaView";

const MyDeskCard = ({ ...rest }: React.HTMLAttributes<HTMLElement>) => {
  const theme = useTheme();
  const isSmall = useMediaView("small");

  const { data: currentUser } = useUserDetailQuery();

  /* 문서 데이터 */
  const { data: workDocuments } = useLatestWorkQuery();

  const { data: analysisDocuments } = useLatestAnalysisQuery();

  /* 탭 */
  const [tabIndex, setTabIndex] = useRecoilState(dashboardMyDeskTabIndexState);
  const tabInfo = useMemo(() => {
    return [
      {
        label: "WORK",
        categoryUuid: DOCUMENT_CATEGORY_UUID.WORK,
        count: workDocuments?.totalElements,
      },
      {
        label: "ANALYSIS",
        categoryUuid: DOCUMENT_CATEGORY_UUID.ANALYSIS,
        count: analysisDocuments?.totalElements,
      },
    ] as const;
  }, [workDocuments, analysisDocuments]);

  /* 문서 상태별 카운트 */
  const { data: countInfo } = useDocumentStatusCountQuery({
    categoryUuid:
      tabIndex === 0
        ? DOCUMENT_CATEGORY_UUID.WORK
        : DOCUMENT_CATEGORY_UUID.ANALYSIS,
  });

  /* 문서 상세 페이지 이동 */
  const navigate = useNavigate();

  /**
   * @description 테이블 행 클릭 이벤트 핸들러
   */
  const onClickRow = (to: To) => {
    navigate(to);
  };

  /**
   * @description 문서 상태별 MY DESK 이동
   */
  const navigateAccordingToStatus = (
    status: DocumentMyDeskResponse["status"],
  ) => {
    navigate({
      pathname: ROUTER_PATH.myPage.desk,
      search: `?selectedTab.uuid=${tabInfo[tabIndex].categoryUuid}&selectedTab.name=${tabInfo[tabIndex].label}&documentStatus=${status}&page=1`,
    });
  };

  return (
    <BoardCard {...rest} title="My Desk" to={ROUTER_PATH.myPage.desk}>
      <TabButtonGroupStyle className="pc-only">
        {tabInfo.map((tab, index) => {
          return (
            <DashboardTab
              key={tab.label}
              count={tab.count || 0}
              active={index === tabIndex}
              onClick={() => setTabIndex(index)}
            >
              {tab.label}
            </DashboardTab>
          );
        })}
      </TabButtonGroupStyle>

      <MobileTabButtonGroupStyle className="mobile-only">
        {tabInfo.map((tab, index) => {
          return (
            <Tab
              key={tab.label}
              selected={index === tabIndex}
              onClick={() => setTabIndex(index)}
            >
              {tab.label} ({tab.count || 0})
            </Tab>
          );
        })}
      </MobileTabButtonGroupStyle>

      <StatusStasticsStyle>
        <DashboardStastic
          label="결재대기"
          icon={<HourglassIcon />}
          iconBgColor={theme.color.brick[300]}
          onClick={() => navigateAccordingToStatus("APPROVE_IN_PROGRESS")}
        >
          {countInfo?.documentStatusCount.approveInProgressCount || 0}개
        </DashboardStastic>
        <DashboardStastic
          label="결재완료"
          icon={<FileCheckIcon />}
          iconBgColor={theme.color.brick[500]}
          onClick={() => navigateAccordingToStatus("APPROVE")}
        >
          {countInfo?.documentStatusCount.approveCount || 0}개
        </DashboardStastic>
        <DashboardStastic
          label="보완요청"
          icon={<CancelIcon />}
          iconBgColor={theme.color.brick[400]}
          onClick={() => navigateAccordingToStatus("REQ_SUPPLEMENT")}
        >
          {countInfo?.documentStatusCount.reqSupplementCount || 0}개
        </DashboardStastic>
        <DashboardStastic
          label="임시저장"
          icon={<FileLockIcon />}
          iconBgColor={theme.color.grey[500]}
          onClick={() => navigateAccordingToStatus("TEMP")}
        >
          {countInfo?.documentStatusCount.tempCount || 0}개
        </DashboardStastic>
      </StatusStasticsStyle>

      <DashboardTable.Table showHead>
        <TheadStyle>
          <DashboardTable.Tr>
            <DashboardTable.Th>No.</DashboardTable.Th>
            <DashboardTable.Th>제목</DashboardTable.Th>
            <DashboardTable.Th>작성자</DashboardTable.Th>
            <DashboardTable.Th>등록일</DashboardTable.Th>
            <DashboardTable.Th>상태</DashboardTable.Th>
          </DashboardTable.Tr>
        </TheadStyle>
        <DashboardTable.Tbody>
          {tabIndex === 0
            ? renderTableRows({
                currentUser,
                documents: workDocuments,
                currentTab: tabInfo[0].label,
                isSmall,
                onClickRow,
              })
            : renderTableRows({
                currentUser,
                documents: analysisDocuments,
                currentTab: tabInfo[1].label,
                isSmall,
                onClickRow,
              })}
        </DashboardTable.Tbody>
      </DashboardTable.Table>
    </BoardCard>
  );
};

export default MyDeskCard;

interface RenderTableRowsProps {
  currentUser?: UserDetailResponse;
  documents?: PageResponseDocumentMyDeskResponse;
  currentTab: "WORK" | "ANALYSIS";
  isSmall: boolean;
  onClickRow: (to: To) => void;
}

const renderTableRows = ({
  currentUser,
  documents,
  currentTab,
  isSmall,
  onClickRow,
}: RenderTableRowsProps) => {
  if (!documents) return;

  const isAnalysis = currentTab === "ANALYSIS";

  let tempSavePath = ROUTER_PATH.document.work.create.form;
  let detailPath = ROUTER_PATH.document.work.detail;

  if (isAnalysis) {
    tempSavePath = ROUTER_PATH.document.analysis.create.form;
    detailPath = ROUTER_PATH.document.analysis.detail;
  }

  return documents.contents.map((document, index) => {
    // if (currentUser && document) {
    //   checkDocumentAccessType({ user: currentUser, document });
    // }

    const isMyDocument = currentUser?.uuid === document.userUuid;
    const isTempSave =
      EDITABLE_DOCUMENT_STATUS_LIST.includes(document.status) && isMyDocument;

    const handleClickRow = () => {
      let to;

      if (isTempSave) {
        if (document.name !== "휴가신청서" && isSmall) {
          modalStore.register({
            id: "warn-modal",
            title: "등록/수정 불가",
            content: "문서 등록은 WEB에서만 가능합니다.",
          });
        } else {
          to = tempSavePath(document.uuid);
        }
      } else {
        to = detailPath(document.uuid);
      }

      if (to) onClickRow(to);
    };

    return (
      <TrStyle key={document.uuid} onClick={handleClickRow}>
        <IndexColumnStyle>
          {String(documents.totalElements - index).padStart(5, "0")}
        </IndexColumnStyle>
        <DashboardTable.Td>{document.name}</DashboardTable.Td>
        <DashboardTable.Td>
          {document.additionalFields.createdUserName}
        </DashboardTable.Td>
        <CreatedAtColumnStyle>
          {dayjs(document.createdAt).format(DATE_FORMAT)}
        </CreatedAtColumnStyle>
        <DashboardTable.Td>
          <DocumentStatus status={document.status} />
        </DashboardTable.Td>
      </TrStyle>
    );
  });
};

const TabButtonGroupStyle = styled.div`
  display: flex;

  & > * {
    flex: 1;
  }
`;

const MobileTabButtonGroupStyle = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 1.6rem;
`;

const StatusStasticsStyle = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  padding: 2.4rem 3rem;

  background: ${({ theme }) => theme.color.background.white};
  border-radius: ${({ theme }) => theme.typography.body1.regular.fontSize};
  border: 0.1rem solid ${({ theme }) => theme.color.stroke.light};

  ${({ theme }) => css`
    ${theme.media.small} {
      justify-content: space-between;
      padding: 2rem;
      background: ${theme.color.background.default};
    }
  `}
`;

const TheadStyle = styled(DashboardTable.Thead)`
  th {
    &:nth-child(1) {
      width: 9.2rem;
    }
    &:nth-child(3) {
      width: 12.8rem;
    }
    &:nth-child(4) {
      width: 12.8rem;
    }
    &:nth-child(5) {
      width: 9.6rem;
    }

    ${({ theme }) => css`
      ${theme.media.small} {
        &:nth-child(3) {
          width: 6rem;
        }
        &:nth-child(4) {
          width: 8rem;
        }
        &:nth-child(5) {
          width: 5.8rem;
        }
      }
    `}
  }
`;

const TrStyle = styled(DashboardTable.Tr)`
  cursor: pointer;
`;

const IndexColumnStyle = styled(DashboardTable.Td)`
  color: ${({ theme }) => theme.color.text.tertiery};
`;

const CreatedAtColumnStyle = styled(DashboardTable.Td)`
  color: ${({ theme }) => theme.color.text.secondary};
`;
